/**
 * Hook for integrating credential blur handlers with Acronis Topics loader.
 *
 * This hook attaches listeners to the schema-driven credential inputs so the
 * topics loader runs only after a user leaves one of the credential fields.
 */

import { type FocusEvent, useEffect } from 'react';

import type { UseAcronisCredentialBlurHandlerParams } from '../AcronisTopics.types';

const ACRONIS_CREDENTIAL_FIELDS = [
  'base_url',
  'client_id',
  'client_secret'
] as const;

/**
 * Enhances credential field blur handlers for Acronis Topics loading.
 *
 * This hook intercepts blur events on credential fields and ensures that:
 * 1. React Hook Form's validation runs first
 * 2. Topics API is called after validation (if credentials are valid)
 * 3. Field-specific error messages are displayed
 *
 * @param params - Configuration for blur handler
 */
export function useAcronisCredentialBlurHandler({
  onCredentialBlur
}: UseAcronisCredentialBlurHandlerParams): void {
  useEffect(() => {
    const selector = ACRONIS_CREDENTIAL_FIELDS.map(
      (fieldName) => `input[name="${fieldName}"]`
    ).join(', ');

    const handleFocusOut = (event: Event) => {
      const target = event.target as HTMLInputElement | null;
      const fieldName = target?.name;

      if (
        !fieldName ||
        !ACRONIS_CREDENTIAL_FIELDS.includes(
          fieldName as (typeof ACRONIS_CREDENTIAL_FIELDS)[number]
        )
      ) {
        return;
      }

      void onCredentialBlur().catch((error) => {
        console.error(
          '[AcronisCredentialBlurHandler] failed to load topics',
          error
        );
      });
    };

    const attachedInputs = new Set<HTMLInputElement>();

    const syncListeners = () => {
      const credentialInputs = Array.from(
        document.querySelectorAll<HTMLInputElement>(selector)
      );

      const nextInputs = new Set(credentialInputs);

      attachedInputs.forEach((field) => {
        if (!nextInputs.has(field)) {
          field.removeEventListener('focusout', handleFocusOut);
          attachedInputs.delete(field);
        }
      });

      credentialInputs.forEach((field) => {
        if (attachedInputs.has(field)) {
          return;
        }

        field.addEventListener('focusout', handleFocusOut);
        attachedInputs.add(field);
      });
    };

    syncListeners();

    const observer = new MutationObserver(() => {
      syncListeners();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    return () => {
      observer.disconnect();

      attachedInputs.forEach((field) => {
        field.removeEventListener('focusout', handleFocusOut);
      });
    };
  }, [onCredentialBlur]);
}

/**
 * Helper to create an enhanced onBlur handler for credential fields.
 *
 * Usage in a form component:
 * ```tsx
 * const baseUrlField = register('base_url');
 * const enhancedOnBlur = createEnhancedBlurHandler(
 *   baseUrlField.onBlur,
 *   handleCredentialBlur
 * );
 *
 * <TextField
 *   {...baseUrlField}
 *   onBlur={enhancedOnBlur}
 * />
 * ```
 *
 * @param originalOnBlur - Original React Hook Form onBlur handler
 * @param credentialBlurHandler - Custom handler to call after validation
 * @returns Enhanced onBlur handler
 */
export function createEnhancedBlurHandler(
  originalOnBlur: (e: FocusEvent<HTMLInputElement>) => void,
  credentialBlurHandler: () => Promise<boolean>
) {
  return async (event: FocusEvent<HTMLInputElement>) => {
    // First, call React Hook Form's validation
    originalOnBlur(event);

    // Then call the credential blur handler
    try {
      await credentialBlurHandler();
    } catch (error) {
      // Log error but don't throw - validation/error display is handled by form state
      console.error('Error loading Acronis topics:', error);
    }
  };
}
